/* 작품·관계·컬렉션 규칙. 레퍼런스 app.js / screens*.js / v2-live.js 의 판정 함수를 순수 함수로 옮겼다.
   v2 가 덮어쓴 함수(gridItems·ttOf·cmdsFor·checkGate …)는 v2 쪽 규칙을 따른다. */
import { officialCommand } from '@/features/studio-renew/official-commands'

import type {
  Bootstrap,
  ChargeMode,
  Room,
  Work,
} from '@/features/veluvit/types/api.types'
import type {
  Catalog,
  Char,
  LanePref,
  ProductType,
  Stage,
} from '@/features/veluvit/types/catalog.types'

function ptypeOf(c: Char | undefined): ProductType {
  if (!c) return '캐릭터챗'
  if (c.ptype) return c.ptype
  return /MULTI|다인|앙상블|스토리/.test(String(c.format || ''))
    ? '스토리챗'
    : '캐릭터챗'
}

export function isStoryChar(c: Char | undefined) {
  return ptypeOf(c) === '스토리챗'
}

export function isAdultWork(c: Char | undefined): boolean {
  return !!c && (c.badges.includes('19+') || c.style?.rating === 'unsafe')
}

function laneOn(pref: LanePref | null): 'f' | 'm' | null {
  return pref === 'f' || pref === 'm' ? pref : null
}

/** 공개된 작품만, 성향이 정해져 있으면 그 성향 + 성향 구분 없는(전체 · a) 작품 */
export function inLane(
  catalog: Catalog,
  ids: string[],
  pref: LanePref | null
): string[] {
  const published = ids.filter((id) => !!catalog.chars[id])
  const lane = laneOn(pref)
  return lane
    ? published.filter(
        (id) =>
          catalog.chars[id].lane === lane || catalog.chars[id].lane === 'a'
      )
    : published
}

export function stageOf(catalog: Catalog, aff: number, id?: string): number {
  let s = 0
  stagesFor(catalog, id).forEach((x, i) => {
    if (aff >= x.min) s = i
  })
  return s
}

function stagesFor(catalog: Catalog, id?: string): Stage[] {
  return (id && catalog.stagesById[id]) || catalog.stages
}

export function stageAt(catalog: Catalog, id: string, aff: number): Stage {
  const list = stagesFor(catalog, id)
  return (
    list[stageOf(catalog, aff, id)] ||
    list[0] || { k: '', min: 0, label: '', color: '', tone: '' }
  )
}

export function workFor(catalog: Catalog, id: string): Work | undefined {
  return catalog.works.find((w) => w.id === id)
}

/**
 * 한 턴(다시 쓰기·이어서 말하기 포함) 젬 — 방에서 고른 모델의 모드별 요금(/api/model/ cost)이 먼저,
 * 없으면 서버 모드 기본값(config.modes), 그것도 없으면 비용 미확정으로 둔다.
 */
export function turnCost(
  catalog: Catalog,
  session: Bootstrap | null,
  room: Room | undefined,
  mode: string
): number | null {
  const config = session?.config
  const model = config?.models?.find(
    (m) => m.id === (room?.model || config.model)
  )
  const cost =
    model?.cost?.[mode as ChargeMode] ??
    config?.modes?.find((m) => m.id === mode)?.cost ??
    catalog.modes.find((m) => m.k === mode)?.cost
  return typeof cost === 'number' && Number.isFinite(cost) && cost >= 0
    ? cost
    : null
}

export type GridItem = {
  i: number
  id: string
  title?: string
  place: string
  no: number
  img: string
  pos: string
  adult: boolean
  gate: Record<string, unknown>
  cond: string
  cardId: string
}

export function gridItems(catalog: Catalog, workId: string): GridItem[] {
  const work = workFor(catalog, workId)
  const cards = (work?.cards || []).filter(
    (card) => card.available !== false && (card.image || card.locked)
  )
  return cards.map((card, index) => {
    const sourceId = card.sourceCardId || card.id
    const gate: Record<string, unknown> = { ...(card.gate || {}) }
    return {
      i: index,
      id: sourceId,
      cardId: card.id,
      title: card.title,
      place: card.place || '기억 속 장면',
      no: index + 1,
      img: card.image || '',
      pos: '50% 30%',
      adult: !!card.adult,
      gate,
      cond: card.description || '대화를 이어가며 장면의 조건을 만나 보세요.',
    }
  })
}

/** 계정이 가진 카드 → 작품별 해금 표·모은 수·장소별 수 (v2 useOwnership) */
export type Ownership = {
  unlocked: Set<string>
  owned: Record<string, number>
  placeExtra: Record<string, Record<string, number>>
}

export function ownershipOf(
  catalog: Catalog,
  session: Pick<Bootstrap, 'ownedCards'> | null
): Ownership {
  const unlocked = new Set<string>()
  const owned: Record<string, number> = {}
  const placeExtra: Record<string, Record<string, number>> = {}
  for (const id of catalog.order) {
    owned[id] = 0
    placeExtra[id] = {}
  }
  for (const card of session?.ownedCards || []) {
    const workId = card.workId
    if (!catalog.chars[workId]) continue
    const work = workFor(catalog, workId)
    const record = work?.cards?.find((c) => c.id === (card.cardId || card.id))
    const sourceId =
      record?.sourceCardId || record?.id || card.sourceCardId || card.cardId
    const item = gridItems(catalog, workId).find((it) => it.id === sourceId)
    if (!item) continue
    if (unlocked.has(`${workId}:${item.i}`)) continue
    unlocked.add(`${workId}:${item.i}`)
    owned[workId] = (owned[workId] || 0) + 1
    placeExtra[workId] ||= {}
    placeExtra[workId][item.place] = (placeExtra[workId][item.place] || 0) + 1
  }
  return { unlocked, owned, placeExtra }
}

export function placeLabel(
  catalog: Catalog,
  own: Ownership,
  workId: string,
  place: string
) {
  const count = gridItems(catalog, workId).filter(
    (card) => card.place === place
  ).length
  return count
    ? `${own.placeExtra[workId]?.[place] || 0}/${count}`
    : '장면 준비 중'
}

/* ---------- 해금 게이트 ---------- */
export type GateFail = { msg: string; need: number | null; now: number | null }

export function checkGate(
  catalog: Catalog,
  room: Room | undefined,
  workId: string,
  item: GridItem
): GateFail[] {
  const progress = room?.cardProgress?.find((p) => p.cardId === item.cardId)
  if (progress) {
    return progress.conditions
      .filter((c) => !c.met)
      .map((c) => ({
        msg: c.label,
        need: typeof c.required === 'number' ? c.required : null,
        now: typeof c.current === 'number' ? c.current : null,
      }))
  }
  // 방이 없으면 진행도는 전부 0 이다 — 레퍼런스 nativeCheckGate 와 같은 문구
  const g = item.gate as Record<string, unknown>
  const fail: GateFail[] = []
  if (g.affinity)
    fail.push({
      msg: `호감도 ${g.affinity}%`,
      need: Number(g.affinity),
      now: 0,
    })
  if (g.affinityNatural)
    fail.push({
      msg: `대화로 쌓은 호감도 ${g.affinityNatural}%`,
      need: Number(g.affinityNatural),
      now: 0,
    })
  if (g.visits)
    fail.push({
      msg: `‘${String(g.place || item.place)}’ ${g.visits}회 방문`,
      need: Number(g.visits),
      now: 0,
    })
  if (g.turns)
    fail.push({ msg: `대화 ${g.turns}턴`, need: Number(g.turns), now: 0 })
  // 장면(분기)에 들어가면 받는 카드 — 스토리 앨범에서 그 장면으로 들어간다
  if (g.branch) {
    const branch = workFor(catalog, workId)?.branches?.find(
      (b) => b.id === g.branch
    )
    fail.push({
      msg: `‘${branch?.title || item.place}’ 장면에 들어가기`,
      need: null,
      now: null,
    })
  }
  if (g.chapter) {
    const chapter = catalog.chapters[workId]?.chapters.find(
      (c) => c.id === g.chapter
    )
    fail.push({
      msg: `${chapter?.title || String(g.chapter)} 완료`,
      need: null,
      now: null,
    })
  }
  for (const flag of (g.flags as string[] | undefined) || [])
    fail.push({ msg: `「${flag}」 사건을 겪어야 해요`, need: null, now: null })
  return fail
}

/* ---------- 명령어 ---------- */
/** k 는 입력 키(!카톡), name 은 보이는 이름(카톡) — 서비스 기본 명령어(카톡 · 에타 · 폰 …)는 키가 아니라 이름으로 보인다(2026-10-13) */
export type CommandRow = {
  k: string
  name: string
  cost: number
  desc: string
  tip: string
}

/** v2 cmdsFor — 작품 발행본의 명령어. 산 속마음은 0💎, 스냅은 방이 알려 주는 요금 */
export function cmdsFor(
  catalog: Catalog,
  room: Room | undefined,
  workId: string
): CommandRow[] {
  const work = workFor(catalog, workId)
  return (work?.commands || [])
    .filter(
      (c) => c.available !== false && c.key.startsWith('!') && c.key !== '!호감'
    )
    .map((c) => {
      const cost =
        c.key === '!속마음' && room?.innerUsage?.purchased
          ? 0
          : c.key === '!스냅'
            ? (room?.snapUsage?.cost ?? c.cost)
            : c.cost
      return {
        k: c.key,
        name:
          officialCommand(c.key)?.title || c.title?.trim() || c.key.slice(1),
        cost,
        desc: c.description || c.title || '',
        tip: '현재 대화의 상황을 반영해요.',
      }
    })
}
